// Interest DETAIL owners: one multiple-value autocomplete wired to the
// single-owner add/remove mutations.
//
// PLACEMENT (flagged for human QA): rendered in the detail header `controls` area
// (beside <Download /> + <Actions />) rather than inline with the applicant name.
//
// DESIGN DECISION — access-grant warning UX (flagged for human QA):
// The bulk Assign-Owner modal and the Owner column confirm before adding an owner
// who lacks property access. This detail path DEFERS that warning and adds
// directly; the server remains the source of truth for access.
import React from "react";
import OwnersEditor, { OwnerSummary } from "components/common/owners-editor/owners-editor";
import OwnerMultiEditor from "components/common/owners-editor/owner-multi-editor";
import UnassignedOwner from "pages/dashboard/interests/interests-table/unassigned-owner";
import { useOwnerChanges } from "components/common/owners-editor/use-owner-changes";

type OwnerControlContainerProps = {
  interestId: string;

  /** Owners from the profile query, in the server's display-name order. */
  owners: OwnerSummary[];

  /** Current viewer id, for the pinned "Me (Name)" option. */
  currentUserId: string;

  /** Current viewer display name, for the "Me (Name)" label. */
  currentUserName: string;

  /** UI-only "who can assign" gate; false renders the owners read-only. */
  canAssign?: boolean;

  /** Names the application in an error that arrives after the view moved on. */
  applicantName?: string;
};

const OwnerControlContainer: React.FC<OwnerControlContainerProps> = ({
  interestId,
  owners: initialOwners,
  currentUserId,
  currentUserName,
  canAssign = true,
  applicantName,
}) => {
  const { owners, pendingUserIds, add, remove } = useOwnerChanges(
    interestId,
    initialOwners,
    applicantName,
  );

  if (!canAssign) {
    return (
      <OwnersEditor
        id={`owner-control-${interestId}`}
        owners={owners}
        canEdit={false}
        emptyLabel={<UnassignedOwner />}
      />
    );
  }

  return (
    <OwnerMultiEditor
      id={`owner-control-${interestId}`}
      selectId={`owner-control-select-${interestId}`}
      owners={owners}
      pendingUserIds={pendingUserIds}
      currentUserId={currentUserId}
      currentUserName={currentUserName}
      onAdd={add}
      onRemove={remove}
    />
  );
};

export default OwnerControlContainer;
